// El instructivo del candado: los pasos que se ejecutan EN EL TELÉFONO y, si el flujo lo trae, el
// QR del APK.
import { CustomerLinkQr } from '../CustomerLinkQr';
import type { DeviceEnrollmentStep } from './deviceEnrollmentSteps';

export interface DeviceEnrollmentGuideProps {
    imei: string;
    steps: readonly DeviceEnrollmentStep[];
    /** APK que el QR codifica. Ausente = el flujo no lleva QR; vacío = no llegó la liga. */
    lockAppUrl?: string;
    /** Qué hacer si se saltó un paso. */
    recoveryNote?: string;
}

/**
 * El QR se genera aquí a partir de `lockAppUrl`: el del proveedor es solo una URL a un APK, no un
 * código de aprovisionamiento, así que se puede dibujar con nuestra marca y sin depender de ellos.
 */
export function DeviceEnrollmentGuide({
    imei,
    steps,
    lockAppUrl,
    recoveryNote,
}: DeviceEnrollmentGuideProps) {
    const withQr = lockAppUrl !== undefined;
    return (
        <div className="flex flex-col gap-4">
            <div
                className={
                    withQr
                        ? 'grid gap-4 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]'
                        : 'grid gap-4'
                }
            >
                {withQr && (
                    <div className="flex flex-col gap-2">
                        {lockAppUrl ? (
                            <CustomerLinkQr
                                url={lockAppUrl}
                                title="Código QR para descargar la app del candado"
                                intro="Escanea este código con el equipo del cliente."
                                hint="Descarga la app del candado."
                            />
                        ) : (
                            <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-[12px] leading-relaxed text-muted-foreground">
                                No llegó la liga de la app del candado. Consulta el estado de nuevo;
                                si sigue sin aparecer, repórtalo a soporte.
                            </p>
                        )}
                    </div>
                )}

                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                        En el equipo del cliente
                    </p>
                    <ol className="mt-2 flex flex-col gap-2">
                        {steps.map((step, i) => (
                            <li key={step.action} className="flex gap-2.5">
                                <span className="mt-px flex size-[18px] flex-none items-center justify-center rounded-full bg-muted text-[10.5px] font-bold text-foreground/70">
                                    {i + 1}
                                </span>
                                <span className="min-w-0 text-[12.5px] leading-[1.45]">
                                    <span className="font-semibold">{step.action}</span>
                                    {step.detail && (
                                        <span className="text-muted-foreground">
                                            {' — '}
                                            {step.detail}
                                        </span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ol>
                    {recoveryNote && (
                        <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">
                            {recoveryNote}
                        </p>
                    )}
                </div>
            </div>

            {/* 🔴 No hay botón para corregirlo: cambiar de IMEI exige soltar el marcador único
                viejo y re-apuntar el expediente MDM, y ninguna de las dos cosas existe todavía. */}
            <div className="border-t border-dashed border-border pt-3">
                <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                    IMEI registrado:{' '}
                    <span className="font-mono tabular-nums tracking-[0.06em] text-foreground">
                        {imei}
                    </span>
                    {' — '}queda ligado al crédito y todavía no se puede cambiar. Si está
                    equivocado, la única salida es cancelar la venta y volver a levantarla.
                </p>
            </div>
        </div>
    );
}
